// The Layout Builder catalog comes from the public, unauthenticated adapter-alignment endpoint: the
// request carries no company or user identity and the response is the same for everyone, so one
// copy per URL is shared for the whole page session. Kept free of portal config so the signed-out
// planner build can use it too.
export const CATALOG_TTL_MS = 10 * 60 * 1000
// A stalled request must not leave the editor masked forever: past this it fails into the error and Retry path.
export const CATALOG_FETCH_TIMEOUT_MS = 20 * 1000

// url -> { promise, value, fetchedAt }. `value` is set once the load succeeds; a failure is dropped.
const entries = new Map()

const fetchCatalog = async catalogApiUrl => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(new Error('Catalog request timed out')), CATALOG_FETCH_TIMEOUT_MS)
  try {
    const [integrationsResponse, applicationsResponse] = await Promise.all([
      fetch(`${catalogApiUrl}/adapter-alignment?type=integrations`, { signal: controller.signal }),
      fetch(`${catalogApiUrl}/adapter-alignment?type=applications`, { signal: controller.signal })
    ])
    if (!integrationsResponse.ok || !applicationsResponse.ok) throw new Error('Catalog request failed')
    const [integrations, applications] = await Promise.all([
      integrationsResponse.json(),
      applicationsResponse.json()
    ])
    if (!Array.isArray(integrations) || !Array.isArray(applications)) {
      throw new Error('Catalog response is invalid')
    }
    return { integrations, applications }
  } finally {
    clearTimeout(timer)
  }
}

const isExpired = entry => entry.value && Date.now() - entry.fetchedAt >= CATALOG_TTL_MS

// The loaded catalog when a fresh one is cached, otherwise null.
export const cachedLayoutCatalog = catalogApiUrl => {
  const entry = entries.get(catalogApiUrl)
  return entry?.value && !isExpired(entry) ? entry.value : null
}

// Resolves to the catalog, sharing an in-flight or fresh load. `force` always starts a new load.
export const loadLayoutCatalog = (catalogApiUrl, { force = false } = {}) => {
  const existing = entries.get(catalogApiUrl)
  if (existing && !force && !isExpired(existing)) return existing.promise
  const entry = { value: null, fetchedAt: null }
  entry.promise = fetchCatalog(catalogApiUrl).then(
    value => {
      entry.value = value
      entry.fetchedAt = Date.now()
      return value
    },
    err => {
      if (entries.get(catalogApiUrl) === entry) entries.delete(catalogApiUrl)
      throw err
    }
  )
  entries.set(catalogApiUrl, entry)
  return entry.promise
}

// Starts the load early (before the page that shows the catalog mounts); a failure is retried on use.
export const prefetchLayoutCatalog = catalogApiUrl =>
  loadLayoutCatalog(catalogApiUrl).then(() => {}, () => {})

export const resetLayoutCatalogCache = () => entries.clear()
